/**
 * The teacher guides: which exist, in which order and group. The texts
 * are `content/<file>.md` (German, loaded by `content.ts` with the guide
 * page only); a missing file leaves its section out. Each section has its
 * own page `/anleitungen/<id>` (U21); `/anleitungen` shows them all as
 * cards.
 */

export const GUIDE_GROUPS = [
  { id: 'start', title: 'Los geht’s' },
  { id: 'games', title: 'Spiele' },
  { id: 'questions', title: 'Fragen und Sets' },
] as const;

export interface GuideSection {
  /** Its page: `/anleitungen/<id>`. */
  id: string;
  title: string;
  /** One line under the title and on its card. */
  lead: string;
  group: (typeof GUIDE_GROUPS)[number]['id'];
  /** The game this section explains (tile and "Spiel starten"). */
  gameId?: string;
  file: string;
}

export const SECTIONS: GuideSection[] = [
  {
    id: 'erste-schritte',
    title: 'Erste Schritte',
    lead: 'Vom Set bis zum ersten Spiel: wie eine Stunde mit Schulspiele abläuft.',
    group: 'start',
    file: 'erste-schritte',
  },
  {
    id: 'blitzquiz',
    title: 'Blitzquiz',
    lead: 'Frage an der Tafel, Antwort auf dem Handy. Schnell und richtig gibt mehr Punkte.',
    group: 'games',
    gameId: 'quiz',
    file: 'quiz',
  },
  {
    id: 'eins-zwei-drei',
    lead: 'Die Kinder gehen in das Feld mit der richtigen Antwort. Mit oder ohne Handys.',
    title: '1, 2 oder 3',
    group: 'games',
    gameId: 'eins-zwei-drei',
    file: 'eins-zwei-drei',
  },
  {
    id: 'buzzer',
    title: 'Buzzer',
    lead: 'Wer zuerst drückt, antwortet. Für deine eigenen Fragen, ohne Set.',
    group: 'games',
    gameId: 'buzzer',
    file: 'buzzer',
  },
  {
    id: 'jeopardy',
    title: 'Jeopardy',
    lead: 'Teams wählen Felder auf einem Board mit Kategorien und Punkten.',
    group: 'games',
    gameId: 'jeopardy',
    file: 'jeopardy',
  },
  {
    id: 'teamraetsel',
    lead: 'Die Antworten sind auf die Handys im Team verteilt: Nur wer redet, kommt ans Ziel.',
    title: 'Teamrätsel',
    group: 'games',
    gameId: 'teamraetsel',
    file: 'teamraetsel',
  },
  {
    id: 'jaeger',
    lead: 'Die Klasse in Teams gegen einen Jäger aus dem Wald.',
    title: 'Klasse gegen den Jäger',
    group: 'games',
    gameId: 'jaeger',
    file: 'jaeger',
  },
  {
    id: 'bilderraetsel',
    lead: 'Ein Bild deckt sich nach und nach auf. Wer es früh erkennt, bekommt mehr Punkte.',
    title: 'Bilderrätsel',
    group: 'games',
    gameId: 'bilderraetsel',
    file: 'bilderraetsel',
  },
  {
    id: 'kreuzwortraetsel',
    lead: 'Ein Kreuzworträtsel an der Tafel, alle Teams lösen gleichzeitig.',
    title: 'Kreuzworträtsel',
    group: 'games',
    gameId: 'kreuzwort',
    file: 'kreuzwort',
  },
  {
    id: 'fakt-oder-fake',
    lead: 'Behauptungen, Schlagzeilen und Bilder prüfen: abstimmen, begründen, auflösen.',
    title: 'Fakt oder Fake',
    group: 'games',
    gameId: 'faktfake',
    file: 'faktfake',
  },
  {
    id: 'erklaer-es',
    title: 'Erklär es',
    lead: 'Fachbegriffe erklären, ohne die verbotenen Wörter zu sagen.',
    group: 'games',
    gameId: 'erklaeres',
    file: 'erklaeres',
  },
  {
    id: 'werwolf',
    title: 'Werwolf',
    lead: 'Das bekannte Rollenspiel, die Handys verteilen die Rollen.',
    group: 'games',
    gameId: 'werwolf',
    file: 'werwolf',
  },
  {
    id: 'sets',
    title: 'Fragensets und Editor',
    lead: 'Sets anlegen, bearbeiten, aus Vorlagen übernehmen.',
    group: 'questions',
    file: 'sets',
  },
  {
    id: 'textformat-blitzquiz',
    title: 'Textformat: Blitzquiz',
    lead: 'Ein ganzes Quiz als Text einfügen.',
    group: 'questions',
    file: 'quiz-textformat',
  },
  {
    id: 'textformat-jeopardy',
    title: 'Textformat: Jeopardy',
    lead: 'Ein ganzes Board als Text einfügen.',
    group: 'questions',
    file: 'jeopardy-textformat',
  },
  {
    id: 'fragenbibliothek',
    title: 'Fragenbibliothek',
    lead: 'Fragen sammeln, teilen und für jedes Spiel nutzen.',
    group: 'questions',
    file: 'fragenbibliothek',
  },
  {
    id: 'quiz-generator',
    title: 'Quiz-Generator',
    lead: 'Ein fertiges Quiz aus der Fragenbibliothek, in einer Minute.',
    group: 'questions',
    file: 'quiz-generator',
  },
  {
    id: 'medien',
    title: 'Bilder, Töne, Videos',
    lead: 'Die Mediathek: hochladen, ansehen, in Sets einbauen.',
    group: 'questions',
    file: 'medien',
  },
];

/** The section that explains a game (format pages link to it). */
export function guideIdFor(gameId: string): string | null {
  return SECTIONS.find((s) => s.gameId === gameId)?.id ?? null;
}
